import { blue, green, orange, red } from '@mui/material/colors'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import {
  type PropsWithChildren,
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'

export const LightTheme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    fontWeightBold: 600,
  },
  palette: {
    mode: 'light',
    background: {
      default: '#F2F2EF',
      paper: '#fff',
    },
    primary: {
      main: '#1D1B20',
      light: '#eceeea',
      dark: '#4d4d4a',
      contrastText: '#fff',
    },
    secondary: {
      main: '#FFFFFF',
      light: '#d9d9d6',
      dark: '#d9d9d6',
      contrastText: '#1D1B20',
    },
    text: {
      primary: '#111111',
      secondary: '#666663',
    },
    warning: {
      main: orange[100],
      dark: orange[800],
    },
    success: {
      main: green[100],
      light: green[400],
    },
    error: {
      main: red[800],
      light: red[400],
    },
    info: {
      main: blue[100],
      light: blue[400],
    },
  },
  components: {
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:after': {
            borderBottomColor: '#222',
          },
          '&:before': {
            borderBottomColor: '#bbb',
          },
        },
        input: {
          color: '#111',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: '#111 !important',
          '&:-webkit-autofill': {
            WebkitTextFillColor: '#111',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#111 !important',
          '&.Mui-focused': {
            color: '#111 !important',
          },
          '&.Mui-disabled': {
            color: '#bbb !important',
          },
        },
      },
    },
  },
})

export const DarkTheme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    fontWeightBold: 600,
  },
  palette: {
    mode: 'dark',
    background: {
      default: '#333330',
      paper: '#1E1E1E',
    },
    primary: {
      main: '#fff',
      light: '#3a3a3a',
      dark: '#80807d',
      contrastText: '#1e1e1e',
    },
    secondary: {
      main: '#5C5C5C',
      light: '#3a3a36',
      dark: '#1a1a17',
      contrastText: '#fff',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    warning: {
      main: orange[200],
      dark: orange[300],
    },
    success: {
      main: green[200],
    },
    error: {
      main: red[200],
    },
    info: {
      main: blue[200],
    },
  },
  components: {
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:after': {
            borderBottomColor: '#fff',
          },
          '&:before': {
            borderBottomColor: '#fff',
          },
        },
        input: {
          color: '#fff',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: '#fff !important',
          '&:-webkit-autofill': {
            WebkitTextFillColor: '#fff',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#fff !important',
          '&.Mui-focused': {
            color: '#fff !important',
          },
          '&.Mui-disabled': {
            color: '#888 !important',
          },
        },
      },
    },
  },
})

type ColorModeContextType = {
  mode: string
  setMode: () => void
}

export const ColorModeContext = createContext<ColorModeContextType>({} as ColorModeContextType)

export const ColorModeContextProvider = ({ children }: PropsWithChildren) => {
  const colorModeFromLocalStorage = localStorage.getItem('colorMode')
  const isSystemPreferenceDark = window?.matchMedia('(prefers-color-scheme: dark)').matches

  const systemPreference = isSystemPreferenceDark ? 'dark' : 'light'
  const [mode, setMode] = useState(colorModeFromLocalStorage || systemPreference)

  useEffect(() => {
    window.localStorage.setItem('colorMode', mode)
  }, [mode])

  const setColorMode = useCallback(() => {
    setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'))
  }, [])

  const contextValue = useMemo(
    () => ({
      setMode: setColorMode,
      mode,
    }),
    [mode, setColorMode],
  )

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={mode === 'light' ? LightTheme : DarkTheme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  )
}
export { CARD_COLORS } from './cardColors'
